import { useState } from "react";
import { useNavigate } from "react-router";

import { useRootStore } from "store";
import { clearCompanyName, logoutUser } from "common/MetaActions";
import { SystemUserRole } from "common/SystemUserRole";
import logo from "assets/images/LUDVIG_CO_LOGO_RGB_WHITE.svg";
import MenuItemDivider from "components/menu/MenuItemDivider";
import Link from "components/link/Link";
import config from "config/config";
import { MenuItem } from "models/header/menuItems";
import { useGlobalContext } from "app/store/GlobalContext";
import LogoutButton from "./components/LogoutButton";
import MainHeaderMenu from "./components/mainMenu/MainMenu";
import MainMenuFullscreen from "./components/mainMenu/MainMenuFullscreen";
import UserHeaderMenu from "./components/mainMenu/dropdownMenu/UserHeaderMenu";
import ExternalUserCustomerMenu from "./components/mainMenu/dropdownMenu/ExternalUserCustomerMenu";
import InternalUserCustomerMenu from "./components/mainMenu/dropdownMenu/InternalUserCustomerMenu";

import "./Header.scss";

export default function Header() {
  const { handleNavigation } = useGlobalContext();
  const navigate = useNavigate();
  const { user, global } = useRootStore((state) => state);
  const { myCustomers } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customers } = myCustomers;

  const { isAuthenticated } = user;

  const { setWorkingCustomerId, staticInformation, resetCustomer } =
    useRootStore((state) => state.global);

  const isInternalUser = user.userInfo && user.userInfo.type === "Internal";

  const [toggleFullScreenMenu, setToggleFullScreenMenu] = useState(false);

  const handleHomeClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>,
    onCloseMenu?: () => void
  ) => {
    e.preventDefault();

    const clickEvent = () => {
      if (onCloseMenu) {
        onCloseMenu();
      }
    };

    if (isAuthenticated) {
      if (isInternalUser) {
        handleNavigation({
          url: "/customers",
          eventTitle: "customers",
          e: e,
          clickEvent
        });
      } else {
        handleNavigation({
          url: "/dashboard",
          eventTitle: "dashboard",
          e: e,
          clickEvent
        });
      }
    } else {
      handleNavigation({ url: "/", eventTitle: "root", e: e, clickEvent });
    }
  };

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };

  const handleChangeCustomerNavigation = (customerId: string) => {
    if (customerId !== global.workingCustomerId) {
      handleNavigation({
        url: "/dashboard",
        clickEvent: () => handleChangeCustomer(customerId)
      });
    }
  };

  const handleChangeCustomer = async (customerId: string) => {
    clearCompanyName();
    resetCustomer();
    setWorkingCustomerId(customerId);
    navigate(`/dashboard`);
  };

  const renderLogo = () => {
    let logoText = "MittKontor";
    if (config.stage !== "production") {
      logoText += ` (${config.stage?.toUpperCase()})`;
    }

    return (
      <div className="logoHeader">
        <img
          alt="Ludvig &amp; Co"
          className="mr-4"
          src={logo}
          onClick={(e) => {
            handleHomeClick(e, () => setToggleFullScreenMenu(false));
          }}
          style={{ cursor: "pointer" }}
        />
        <div className="logoText">{logoText}</div>
      </div>
    );
  };

  const getOrganizationsMenu = () => {
    if (isInternalUser) {
      return [
        {
          component: (
            <InternalUserCustomerMenu
              handleChangeCustomer={handleChangeCustomerNavigation}
            />
          ),
          title: "Internal customers"
        },
        { component: <MenuItemDivider /> }
      ];
    }

    const externalUserHasMultipleOperations = customers.length > 1;

    if (externalUserHasMultipleOperations) {
      return [
        {
          component: (
            <ExternalUserCustomerMenu
              handleChangeCustomer={handleChangeCustomerNavigation}
            />
          )
        },
        { component: <MenuItemDivider /> }
      ];
    }

    return [];
  };

  const getAdminMenu = () => {
    const isAdmin =
      user.userInfo && user.userInfo.systemUserRole === SystemUserRole.Admin;

    return isAdmin
      ? [
          {
            href: "/admin",
            title: "Admin"
          },
          { component: <MenuItemDivider /> }
        ]
      : [];
  };

  const getHelpMenu = () => {
    const allUsersHelpLink = {
      component: (
        <Link
          to={
            staticInformation?.supportContactInformation.externalSupportSite ??
            ""
          }
          target="_blank"
          title="Hjälpen kommer att öppnas i eget fönster"
        >
          Hjälp
        </Link>
      ),
      title: "Hjälp"
    };

    const internalUsersHelpLink = isInternalUser
      ? [
          {
            component: (
              <Link
                to={
                  staticInformation?.supportContactInformation
                    .internalSupportSite ?? ""
                }
                target="_blank"
                title="Hjälpen kommer att öppnas i eget fönster"
              >
                Konsultsupport
              </Link>
            )
          }
        ]
      : [];

    return [allUsersHelpLink, ...internalUsersHelpLink];
  };

  const userMenuComponent = {
    component: <UserHeaderMenu />,
    title: "User"
  };

  const logoutComponent = {
    title: "Logga ut",
    component: <LogoutButton onLogout={handleLogout} />,
    info: "När du klickar på Logga ut lämnar du MittKontor och kommer till MittKontors inloggningssida."
  };

  const helpComponents = getHelpMenu();
  const adminMenuComponents = getAdminMenu();
  const organizationsMenuComponents = getOrganizationsMenu();

  const dividerComponent = {
    component: <MenuItemDivider />
  };

  const menuItems: MenuItem[] = [
    ...adminMenuComponents,
    userMenuComponent,
    dividerComponent,
    ...organizationsMenuComponents,
    ...helpComponents,
    logoutComponent
  ];

  const isLoggedIn = user.isAuthenticated;

  return (
    <header className="header no-print">
      <div className="headerContent">
        {renderLogo()}

        {isLoggedIn && (
          <>
            <MainHeaderMenu menuItems={menuItems} />
            <MainMenuFullscreen
              menuItems={menuItems}
              toggleFullScreenMenu={toggleFullScreenMenu}
              setToggleFullScreenMenu={setToggleFullScreenMenu}
            />
          </>
        )}
      </div>
    </header>
  );
}
